/* eslint-disable */

import Vue from 'vue';
import axios from 'axios';
import { generateRoomDisplayName } from './chat-utils';
import { safeSnapshot } from './firestoreListen';
import { 
    collection, 
    query, 
    where, 
    orderBy, 
    limit, 
    addDoc, 
    updateDoc, 
    doc, 
    getDocs, 
    deleteDoc, 
    arrayUnion, 
    arrayRemove, 
    increment,
    startAfter,
    endAt,
    getDoc,
    serverTimestamp,
    setDoc,
    writeBatch
} from 'firebase/firestore';

// Helper to format time in 12-hour format with AM/PM
function formatTime12Hour(date) {
    let hours = date.getHours();
    const minutes = zeroPad(date.getMinutes(), 2);
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    return `${hours}:${minutes}${ampm}`;
}

function parseTimestamp(timestamp, format = '') {
    if (!timestamp) return;

    const date = timestamp.seconds
        ? new Date(timestamp.seconds * 1000)
        : timestamp;

    if (format === 'HH:mm') {
        // Changed from 24-hour to 12-hour format with AM/PM
        return formatTime12Hour(date);
    } else if (format === 'DD MMMM YYYY') {
        const options = { month: 'long', year: 'numeric', day: 'numeric' };
        return `${new Intl.DateTimeFormat('en-GB', options).format(date)}`;
    } else if (format === 'DD/MM/YY') {
        const options = { month: 'numeric', year: 'numeric', day: 'numeric' };
        return `${new Intl.DateTimeFormat('en-GB', options).format(date)}`;
    } else if (format === 'MM/DD/YY') {
        return `${new Intl.DateTimeFormat('en-US').format(date)}`;
    } else if (format === 'MM/DD/YYYY') {
        return `${new Intl.DateTimeFormat('en-US').format(date)}`;
    } else if (format === 'DD MMMM, HH:mm') {
        const options = { month: 'long', day: 'numeric' };
        return `${new Intl.DateTimeFormat('en-GB', options).format(
            date
        )}, ${formatTime12Hour(date)}`;
    }

    return date;
}

const zeroPad = (num, pad) => {
    return String(num).padStart(pad, '0');
};

function isSameDay(d1, d2) {
    return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
    );
}

function formatLastMessage(message, currentUserId, dateFormatStr) {
    if (!message.timestamp) return;
    const date = new Date(message.timestamp.seconds * 1000);
    const timestampFormat = isSameDay(date, new Date()) ? 'HH:mm' : dateFormatStr;

    let timestamp = parseTimestamp(message.timestamp, timestampFormat);

    if (timestampFormat === 'HH:mm') timestamp = `Today, ${timestamp}`;
    let content = message.content;
    if (message.file) {
        // Show clean filename in room preview
        // file.name may already include extension (e.g. "index.pdf") or not (e.g. "index")
        const name = message.file.name || 'File';
        const hasExtInName = name.includes('.');
        content = hasExtInName ? name : `${name}`;
    }
    return {
        ...message,
        ...{
            senderId: message.sender_id, // Add vue-advanced-chat compatibility
            content,
            timestamp,
            date: message.timestamp.seconds,
            seen: message.sender_id === currentUserId ? message.seen : null,
            new:
                message.sender_id !== currentUserId &&
                (!message.seen || !message.seen[currentUserId]),
            // Add delivery status properties for vue-advanced-chat compatibility
            saved: true, // Firebase messages are always saved once they exist
            distributed: true, // Firebase messages are always distributed once they exist
        }
    };
}

function formatMessage(room, message, dateFormatStr) {
    const senderUser = room.users.find(
        user => message.data().sender_id === user.employeeID
    );
    const { sender_id, timestamp, seen } = message.data();
    const messageData = message.data();
    
    // Ensure file messages always have valid content (library requires non-null string)
    // For file messages, use the filename as content if content is missing
    let content = messageData.content;
    if (messageData.file && (!content || content === '')) {
        content = messageData.file.name || 'File';
    }

    return {
        ...messageData,
        ...{
            content,
            sender_id,
            senderId: sender_id, // Add vue-advanced-chat compatibility
            _id: message.id,
            seconds: timestamp.seconds,
            timestamp: parseTimestamp(timestamp, 'HH:mm'),
            date: parseTimestamp(timestamp, 'DD MMMM YYYY'),
            username: senderUser ? senderUser.username : null,
            // Add delivery status properties for vue-advanced-chat compatibility
            saved: true, // Firebase messages are always saved once they exist
            distributed: true, // Firebase messages are always distributed once they exist
            seen: seen && Object.keys(seen).length > 0 // Convert Firebase seen object to boolean
        }
    };
}

export default {
    namespaced: true,
    state: {
        newChatMessagesAvailable: false,
        newChatMessageTimestamp: 0,
        notificationListenerActive: false,
        dateFormatStr: 'MM/DD/YY',
        currentUserId: null,
        rooms: [],
        startRooms: null,
        endRooms: null,
        roomsLoaded: false,
        loadingRooms: true,
        allUsers: [],
        messages: [],
        messagesLoaded: false,
        roomsListener: [],
        listeners: [],
        roomsLoadedCount: 0,
        startMessages: null,
        endMessages: null,
        loadingLastMessageByRoom: 0,
        latestUnreadMessage: null
    },

    getters: {
        findUser: state => userId => {
            return state.allUsers.find(user => user.employeeID === userId);
        },
        roomIndex: state => roomId => {
            return state.rooms.findIndex(room => room.roomId === roomId);
        },
        messageIndex: state => id => {
            return state.messages.findIndex(message => message._id === id);
        }
    },

    mutations: {
        setNewChatMessagesAvailable(state, payload) {
            state.newChatMessagesAvailable = payload;
        },
        setNewChatMessageTimestamp(state, payload) {
            state.newChatMessageTimestamp = payload;
        },
        setNotificationListenerActive(state, payload) {
            state.notificationListenerActive = payload;
        },
        setLatestUnreadMessage(state, payload) {
            state.latestUnreadMessage = payload;
        },
        setDateFormatStr(state, payload) {
            state.dateFormatStr = payload;
        },
        setCurrentUserId(state, payload) {
            state.currentUserId = payload;
        },
        setRooms(state, payload) {
            state.rooms = payload;
        },
        setStartRooms(state, payload) {
            state.startRooms = payload;
        },
        setEndRooms(state, payload) {
            state.endRooms = payload;
        },
        updateRoomLastMessage(state, payload) {
            if (state.rooms[payload.roomIndex])
                Vue.set(state.rooms[payload.roomIndex], 'lastMessage', payload.lastMessage);
        },
        setLoadingRooms(state, payload) {
            state.loadingRooms = payload;
        },
        setRoomsLoaded(state, payload) {
            state.roomsLoaded = payload;
        },
        setRoomsLoadedCount(state, payload) {
            state.roomsLoadedCount = payload;
        },
        setAllUsers(state, payload) {
            state.allUsers = [...state.allUsers, ...payload];
        },
        setMessages(state, payload) {
            state.messages = payload;
        },
        addMessage(state, payload) {
            state.messages = [...state.messages, ...payload];
        },
        addMessageAtBeginning(state, payload) {
            state.messages.unshift(payload);
        },
        // Added 2026-05-27: batch-prepend older messages (paginated scroll-back).
        // setMessages REPLACES; this PREPENDS so recent messages stay loaded when
        // the user scrolls up to load older history. See cypress/node-tests/chat-pagination.test.js
        prependMessages(state, payload) {
            state.messages = [...payload, ...state.messages];
        },
        updateMessage(state, payload) {
            Vue.set(state.messages, payload.messageIndex, payload.formattedMessage);
        },
        setMessagesLoaded(state, payload) {
            state.messagesLoaded = payload;
        },
        addRoomsListener(state, payload) {
            state.roomsListener.push(payload);
        },
        setRoomsListener(state, payload) {
            state.roomsListener = payload;
        },
        addListener(state, payload) {
            state.listeners.push(payload);
        },
        setListeners(state, payload) {
            state.listeners = payload;
        },
        setStartMessages(state, payload) {
            state.startMessages = payload;
        },
        setEndMessages(state, payload) {
            state.endMessages = payload;
        },
        setLoadingLastMessageByRoom(state, payload) {
            state.loadingLastMessageByRoom = payload;
        },
        setRoomName(state, { roomId, newName }) {
            const idx = state.rooms.findIndex(r => r.roomId === roomId);
            if (idx !== -1) {
                Vue.set(state.rooms[idx], 'roomName', newName);
                Vue.set(state.rooms[idx], 'lastUpdated', new Date());
            }
        }
    },

    actions: {
        async checkIfNewChatMessagesAvailable({ commit, rootState, state }, payload) {
            // Guard against duplicate listeners (e.g. hot-reload)
            if (state.notificationListenerActive) return;
            commit('setNotificationListenerActive', true);
            // Listen to ALL chatrooms the user belongs to.
            // We track lastUpdated per room to detect new messages — this is more
            // reliable than querying unreadMap > 0, because markMessagesSeen can
            // zero out unreadMap before this listener fires when on the chat page.
            const q = query(
                collection(rootState.db, 'chatrooms'),
                where('users', 'array-contains', payload.currentUser)
            );
            let initialSnapshot = true;
            const prevLastUpdated = {};

            const unsubscribe = safeSnapshot(q, async (roomsSnapshot) => {
                // 1) Update unread badge (green dot on sidebar)
                let hasUnreads = false;
                roomsSnapshot.docs.forEach(d => {
                    const room = d.data();
                    if (room.unreadMap && room.unreadMap[payload.currentUser] > 0) {
                        hasUnreads = true;
                    }
                });
                commit('setNewChatMessagesAvailable', hasUnreads);
                if (!hasUnreads) {
                    commit('setLatestUnreadMessage', null);
                }

                // 2) Detect NEW messages by checking which rooms have a newer lastUpdated
                if (!initialSnapshot) {
                    let newestChangedRoomId = null;
                    let newestChangedTime = 0;

                    roomsSnapshot.docChanges().forEach(change => {
                        // A pending change is this browser's own write (a room it just created, a message it just
                        // sent): never an alert, and a just-created room's messages are refused by the demo chat
                        // rule until the server has the room.
                        if (change.doc.metadata && change.doc.metadata.hasPendingWrites) return;
                        if (change.type === 'modified' || change.type === 'added') {
                            const room = change.doc.data();
                            const t = room.lastUpdated?.seconds || 0;
                            const prev = prevLastUpdated[change.doc.id] || 0;
                            // Only consider rooms whose lastUpdated actually advanced
                            if (t > prev && t > newestChangedTime) {
                                newestChangedTime = t;
                                newestChangedRoomId = change.doc.id;
                            }
                        }
                    });

                    if (newestChangedRoomId) {
                        try {
                            const msgSnap = await getDocs(query(
                                collection(rootState.db, 'chatrooms', newestChangedRoomId, 'messages'),
                                orderBy('timestamp', 'desc'),
                                limit(1)
                            ));
                            if (!msgSnap.empty) {
                                const msgData = msgSnap.docs[0].data();
                                // Only notify if the message is from someone else
                                if (msgData.sender_id !== payload.currentUser) {
                                    let senderName = 'Someone';
                                    if (msgData.sender_id) {
                                        const usersSnapshot = await getDocs(
                                            query(collection(rootState.db, 'users'), where('employeeID', '==', msgData.sender_id))
                                        );
                                        if (!usersSnapshot.empty) {
                                            senderName = usersSnapshot.docs[0].data().username || 'Someone';
                                        }
                                    }
                                    commit('setLatestUnreadMessage', {
                                        senderName,
                                        content: msgData.content || 'New message',
                                        senderId: msgData.sender_id
                                    });
                                    commit('setNewChatMessageTimestamp', Date.now());
                                }
                            }
                        } catch (e) {
                            console.warn('[chat] Failed to fetch message details:', e);
                        }
                    }
                }

                // 3) Store current lastUpdated for each room
                roomsSnapshot.docs.forEach(d => {
                    prevLastUpdated[d.id] = d.data().lastUpdated?.seconds || 0;
                });
                initialSnapshot = false;
            }, { source: 'chat/newMessageAlerts' });
            // Do NOT add to roomsListener — ChatContainer.resetRooms() clears that
            // array when the chat page loads/unloads, which would kill this listener.
            // Only register with customer/firestoreListeners for global cleanup (logout/site-switch).
            commit('customer/addFirestoreListener', unsubscribe, {root: true});
        },

        async addChatData({ rootState, dispatch }, payload) {
            try {
                dispatch('alert/setLoading', true, { root: true });

                const user1 = payload.users[0];
                await setDoc(doc(rootState.db, 'users', user1._id), user1);

                const user2 = payload.users[1];
                await setDoc(doc(rootState.db, 'users', user2._id), user2);

                const user3 = payload.users[2];
                await setDoc(doc(rootState.db, 'users', user3._id), user3);

                await addDoc(collection(rootState.db, 'chatrooms'), {
                    users: [user1._id, user2._id, user3._id],
                    lastUpdated: new Date()
                });

                dispatch('alert/setLoaded', true, { root: true });
                dispatch('alert/setMessage', 'Chat data added successfully!', { root: true });
                dispatch('alert/setDismissSuccessCountDown', 1, { root: true });
            } catch (err) {
                console.log('Error adding chat data:', err);
                dispatch('alert/setErrored', true, { root: true });
                dispatch('alert/setMessage', err, { root: true });
            }
        },

        async _fetchMoreRooms({ commit, rootState, getters, dispatch }, payload) {
            let rooms;

            if (rootState.chat.endRooms && !rootState.chat.startRooms) {
                commit('setRoomsLoaded', true);
                return;
            }

            commit('setDateFormatStr', payload.dateFormatStr);
            commit('setCurrentUserId', payload.currentUserId);

            if (!payload.currentUserId) {
                console.warn('[store/chat.js _fetchMoreRooms] Missing currentUserId; aborting rooms fetch');
                commit('setRoomsLoaded', true);
                commit('setLoadingRooms', false);
                return;
            }

            let q = query(
                collection(rootState.db, 'chatrooms'),
                where('users', 'array-contains', payload.currentUserId),
                orderBy('lastUpdated', 'desc'),
                limit(payload.roomsPerPage)
            );

            if (rootState.chat.startRooms) {
                q = query(q, startAfter(rootState.chat.startRooms));
            }

            const unsubscribe = safeSnapshot(q, async (roomsSnapshot) => {
                if (rootState.chat.startRooms) {
                    commit('setEndRooms', rootState.chat.startRooms);
                }
                commit('setStartRooms', roomsSnapshot.docs[roomsSnapshot.docs.length - 1]);

                if (roomsSnapshot.empty || roomsSnapshot.size < payload.roomsPerPage) {
                    commit('setRoomsLoaded', true);
                }

                const roomUserIds = [];
                roomsSnapshot.docs.forEach((room) => {
                    room.data().users.forEach((userId) => {
                        let foundUser = getters.findUser(userId);
                        if (!foundUser && roomUserIds.indexOf(userId) === -1) {
                            roomUserIds.push(userId);
                        }
                    });
                });

                const rawUsers = [];
                for (let userId of roomUserIds) {
                    const usersSnapshot = await getDocs(query(collection(rootState.db, 'users'), where('employeeID', '==', userId)));
                    usersSnapshot.docs.forEach((user) => {
                        let _user = user.data();
                        rawUsers.push(_user);
                    });
                }
                commit('setAllUsers', rawUsers);

                const roomList = {};
                roomsSnapshot.docs.forEach((roomDoc) => {
                    const roomData = roomDoc.data();
                    roomList[roomDoc.id] = { ...roomData, users: [] };
                    roomData.users.forEach((userId) => {
                        const foundUser = getters.findUser(userId);
                        if (foundUser) {
                            roomList[roomDoc.id].users.push(foundUser);
                        }
                    });
                });

                const formattedRooms = [];
                Object.keys(roomList).forEach((key) => {
                    const room = roomList[key];
                    const roomContacts = room.users.filter((user) => user.employeeID !== payload.currentUserId);

                    const finalRoomName = generateRoomDisplayName({
                        storedRoomName: room.roomName,
                        roomUsers: room.users,
                        currentUserId: payload.currentUserId
                    });

                    let roomAvatar = roomContacts.length === 1 && roomContacts[0].avatar ? roomContacts[0].avatar : require('@/assets/teamio-logo.png');
                    // Avoid bad placeholder domains breaking render
                    if (typeof roomAvatar === 'string' && roomAvatar.startsWith('https://via.placeholder.com')) {
                        roomAvatar = require('@/assets/teamio-logo.png');
                    }
                    formattedRooms.push({
                        roomId: key,
                        avatar: roomAvatar,
                        unreadCount: room.unreadMap ? room.unreadMap[payload.currentUserId] : null,
                        ...room,
                        roomName: finalRoomName
                    });
                });

                commit('setRooms', formattedRooms);
                commit('setRoomsLoaded', true);

                // A room this browser just created is in the snapshot BEFORE the server has it (latency compensation).
                // Listening to its messages then is refused by the demo chat rule (it reads the room doc), and that
                // denial forces a token refresh that restarts Firestore's write stream mid-send (2026-10-21: the first
                // message came back "Document already exists" and its badge was lost). _createRoom attaches it once
                // the server has the room.
                const pendingRoomIds = new Set(roomsSnapshot.docs
                    .filter((roomDoc) => roomDoc.metadata && roomDoc.metadata.hasPendingWrites)
                    .map((roomDoc) => roomDoc.id));
                formattedRooms
                    .filter((room) => !pendingRoomIds.has(room.roomId))
                    .forEach((room) => dispatch('_listenLastMessage', room));

                if (!rootState.chat.rooms.length || rootState.chat.rooms.length === 0) {
                    commit('setLoadingRooms', false);
                    commit('setRoomsLoadedCount', 0);
                }
            }, { source: 'chat/rooms' });

            commit('addRoomsListener', unsubscribe);
            commit('customer/addFirestoreListener', unsubscribe, {root: true});
        },

        async _fetchMessages({ commit, rootState, dispatch }, payload) {
            let q = query(
                collection(rootState.db, 'chatrooms', payload.room.roomId, 'messages'),
                orderBy('timestamp', 'desc'),
                limit(payload.messagesPerPage)
            );

            if (rootState.chat.startMessages) {
                q = query(q, startAfter(rootState.chat.startMessages));
            }

            const messagesSnapshot = await getDocs(q);

            if (rootState.chat.startMessages) {
                commit('setEndMessages', rootState.chat.startMessages);
            }

            commit('setStartMessages', messagesSnapshot.docs[messagesSnapshot.docs.length - 1]);

            let listenerQuery = query(
                collection(rootState.db, 'chatrooms', payload.room.roomId, 'messages'),
                orderBy('timestamp')
            );

            if (rootState.chat.startMessages) {
                listenerQuery = query(listenerQuery, startAfter(rootState.chat.startMessages));
            }

            if (rootState.chat.endMessages) {
                listenerQuery = query(listenerQuery, endAt(rootState.chat.endMessages));
            }

            if (payload.options && payload.options.reset) {
                commit('setMessages', []);
            }

            // Batch format all messages first, then commit once to trigger a single
            // watcher update. (Per-message addMessageAtBeginning would re-fire the
            // library's messages watcher per item and broke auto-scroll on first load.)
            const formattedMessages = [];
            messagesSnapshot.docs.forEach((message) => {
                formattedMessages.unshift(formatMessage(payload.room, message, rootState.chat.dateFormatStr));
            });
            // First load (endMessages null) -> setMessages preserves the single-watcher-
            //   update path that the auto-scroll-to-bottom fix depends on.
            // Pagination (endMessages set by line 522-524 above) -> prependMessages so
            //   recent messages stay visible when older ones are loaded.
            // See cypress/node-tests/chat-pagination.test.js for the regression coverage.
            if (rootState.chat.endMessages) {
                commit('prependMessages', formattedMessages);
            } else {
                commit('setMessages', formattedMessages);
            }

            // messagesLoaded gates the library's scroll-to-top trigger:
            // vue-advanced-chat's loadMoreMessages() calls infiniteState.complete()
            // (permanently stops paginating) the moment :messages-loaded is true, and
            // never emits 'fetch-messages' again. So flip it true ONLY on the final
            // page — a page smaller than the requested size means no older messages
            // remain. Setting it true after a FULL page (the old bug) killed scroll-back
            // after the first 20 messages. See cypress/node-tests/chat-scrollback-trigger.test.js
            // (logic + library contract) and cypress/e2e/chat-scrollback.cy.js (real browser).
            commit('setMessagesLoaded', messagesSnapshot.size < (payload.messagesPerPage || 20));

            const unsubscribe = safeSnapshot(listenerQuery, (snapshots) => {
                dispatch('listenMessages', { messages: snapshots, room: payload.room });
            }, { source: 'chat/roomMessages' });

            commit('addListener', unsubscribe);
            commit('customer/addFirestoreListener', unsubscribe, {root: true});
        },

        listenMessages({ commit, rootState, getters }, payload) {
            const cu = rootState.chat.currentUserId;

            // Process only the delta (added/modified) instead of re-iterating
            // the whole snapshot on every echo. Without this, each
            // mark-as-seen write echoes back and re-runs the full loop, which
            // on a 14-person room with ~19 unread on open caused ~466 fires
            // in the first 5s after click. (Profiled 2026-05-07.)
            const changes = typeof payload.messages.docChanges === 'function'
                ? payload.messages.docChanges()
                : null;
            const docsToProcess = changes
                ? changes.filter(c => c.type === 'added' || c.type === 'modified').map(c => c.doc)
                : payload.messages.docs;

            if (docsToProcess.length === 0) return;

            const messagesToMarkSeen = [];
            docsToProcess.forEach((message) => {
                const formattedMessage = formatMessage(payload.room, message, rootState.chat.dateFormatStr);
                const messageIndex = getters.messageIndex(message.id);
                if (messageIndex === -1) {
                    commit('addMessage', [formattedMessage]);
                } else {
                    commit('updateMessage', { messageIndex, formattedMessage });
                }
                const data = message.data();
                if (data.sender_id !== cu && (!data.seen || !data.seen[cu])) {
                    messagesToMarkSeen.push(message);
                }
            });

            // Batch ALL seen writes for this snapshot into one writeBatch:
            // single network round-trip and a single listener echo, instead of
            // 2*N updateDocs each echoing back individually.
            if (messagesToMarkSeen.length > 0) {
                const batch = writeBatch(rootState.db);
                messagesToMarkSeen.forEach((message) => {
                    const messageRef = doc(rootState.db, 'chatrooms', payload.room.roomId, 'messages', message.id);
                    batch.update(messageRef, {
                        [`seen.${cu}`]: new Date(),
                        notseen: arrayRemove(cu)
                    });
                });
                const roomRef = doc(rootState.db, 'chatrooms', payload.room.roomId);
                batch.update(roomRef, { [`unreadMap.${cu}`]: 0 });
                batch.commit().catch((err) => {
                    console.error('[chat] mark-as-seen batch failed', err);
                });
            }
        },

        // Kept for backwards compatibility in case anything outside this module
        // dispatches it. Internal listenMessages no longer uses it.
        async markMessagesSeen({ rootState }, payload) {
            const { room, message, currentUserId } = payload;
            if (message.data().sender_id !== currentUserId && (!message.data().seen || !message.data().seen[currentUserId])) {
                const messageRef = doc(rootState.db, 'chatrooms', room.roomId, 'messages', message.id);
                await updateDoc(messageRef, {
                    [`seen.${currentUserId}`]: new Date(),
                    notseen: arrayRemove(currentUserId)
                });

                const roomRef = doc(rootState.db, 'chatrooms', room.roomId);
                // Use dot notation to only update this user's count, not replace entire unreadMap
                await updateDoc(roomRef, {
                    [`unreadMap.${currentUserId}`]: 0
                });
            }
        },

        _listenLastMessage({ commit, rootState, getters, dispatch }, room) {
            const q = query(
                collection(rootState.db, 'chatrooms', room.roomId, 'messages'),
                orderBy('timestamp', 'desc'),
                limit(1)
            );

            const unsubscribe = safeSnapshot(q, (messages) => {
                messages.docs.forEach((message) => {
                    const lastMessage = formatLastMessage(message.data(), rootState.chat.currentUserId, rootState.chat.dateFormatStr);
                    const roomIndex = getters.roomIndex(room.roomId);
                    commit('updateRoomLastMessage', { roomIndex, lastMessage });
                });

                if (rootState.chat.loadingLastMessageByRoom < rootState.chat.rooms.length) {
                    let temp = rootState.chat.loadingLastMessageByRoom;
                    commit('setLoadingLastMessageByRoom', temp + 1);
                    if (rootState.chat.loadingLastMessageByRoom === rootState.chat.rooms.length) {
                        commit('setLoadingRooms', false);
                        commit('setRoomsLoadedCount', rootState.chat.rooms.length);
                    }
                }
            }, { source: 'chat/lastMessage' });

            commit('addRoomsListener', unsubscribe);
            commit('customer/addFirestoreListener', unsubscribe, {root: true});
        },

        async _sendMessage({ commit, rootState, dispatch }, payload) {
            // The message and the room's unread counts go in ONE batch, under an id made here. addDoc is a create:
            // a write resent after a stream restart failed "Document already exists" and threw before the unread
            // update, so the message landed with no badge. A set under our own id is safe to resend.
            const messageRef = doc(collection(rootState.db, 'chatrooms', payload.roomId, 'messages'));
            const roomRef = doc(rootState.db, 'chatrooms', payload.roomId);
            // Dot notation per recipient: `unreadMap: {...}` would REPLACE the whole map (each count back to 1,
            // the sender's entry dropped). Pinned by tests/component/chatStore.unreadCount.spec.js.
            const roomUpdate = { lastUpdated: new Date() };
            payload.message.notseen.forEach((user) => {
                roomUpdate[`unreadMap.${user}`] = increment(1);
            });

            const batch = writeBatch(rootState.db);
            batch.set(messageRef, payload.message);
            batch.update(roomRef, roomUpdate);
            await batch.commit();

            return messageRef;
        },

        async _createRoom({ commit, rootState, dispatch }, payload) {
            for (let user of payload.users) {
                const id = user.EmployeeID || user.employeeID || user._id;
                const username = user.Name || user.username || 'Unknown';
                const avatarCandidate = user.PictureUrl || user.avatar;
                const userDoc = {
                    _id: id,
                    username,
                    employeeID: id
                };
                if (avatarCandidate !== undefined) {
                    // Firestore does not allow undefined values; include only if defined
                    userDoc.avatar = avatarCandidate || null;
                }
                await setDoc(doc(rootState.db, 'users', id), userDoc, { merge: true });
            }

            const unreadMap = {};
            payload.userIds.forEach((user) => {
                if (user && typeof user === 'string') {
                    unreadMap[user] = 0;
                }
            });
            unreadMap[payload.currentUserId] = 0;

            const roomDataToSave = {
                siteID: payload.siteID,
                users: Array.from(new Set([...payload.userIds, payload.currentUserId])),
                unreadMap: unreadMap,
                lastUpdated: new Date(),
                roomName: payload.roomName
            };

            const roomRef = await addDoc(collection(rootState.db, 'chatrooms'), roomDataToSave);
            // addDoc resolves once the server has the room, so its messages can be listened to now (the rooms
            // listener skipped it while it was pending).
            dispatch('_listenLastMessage', { roomId: roomRef.id });
        },

        async _addRoomUser({ commit, rootState, dispatch }, payload) {
            const { inviteRoomId, users, userIds } = payload;

            const promises = [];
            users.forEach((user) => {
                const id = user.EmployeeID || user.employeeID || user._id;
                const username = user.Name || user.username || 'Unknown';
                const avatarCandidate = user.PictureUrl || user.avatar;
                const userDoc = {
                    _id: id,
                    username,
                    employeeID: id
                };
                if (avatarCandidate !== undefined) {
                    userDoc.avatar = avatarCandidate || null;
                }
                promises.push(setDoc(doc(rootState.db, 'users', id), userDoc, { merge: true }));

                promises.push(updateDoc(doc(rootState.db, 'chatrooms', inviteRoomId), {
                    users: arrayUnion(user.EmployeeID),
                    [`unreadMap.${user.EmployeeID}`]: 0
                }));
            });

            await Promise.all(promises);
        },

        async _editMessage({ commit, rootState, dispatch }, payload) {
            const messageRef = doc(rootState.db, 'chatrooms', payload.roomId, 'messages', payload.messageId);
            const newMessage = { edited: new Date() };
            newMessage.content = payload.newContent;

            if (payload.file) {
                newMessage.file = {
                    name: payload.file.name,
                    size: payload.file.size,
                    type: payload.file.type,
                    extension: payload.file.extension,
                    url: payload.file.url || payload.file.localUrl
                };
                if (payload.file.audio) {
                    newMessage.file.audio = true;
                    newMessage.file.duration = payload.file.duration;
                }
            } else {
                newMessage.file = deleteField();
            }

            await updateDoc(messageRef, newMessage);
        },

        async sendMessageReaction({ state, rootState }, { reaction, remove, messageId, roomId }) {
            if (!state.currentUserId) {
                console.warn('[chat/sendMessageReaction] currentUserId not set; skipping');
                return;
            }
            if (!reaction?.name) return;
            const messageRef = doc(rootState.db, 'chatrooms', roomId, 'messages', messageId);
            const dbAction = remove
                ? arrayRemove(state.currentUserId)
                : arrayUnion(state.currentUserId);
            try {
                await updateDoc(messageRef, { [`reactions.${reaction.name}`]: dbAction });
            } catch (err) {
                console.error('[chat/sendMessageReaction] Failed to update reaction:', err);
                throw err;
            }
        },

        async _deleteMessage({ commit, rootState, dispatch }, payload) {
            const messageRef = doc(rootState.db, 'chatrooms', payload.roomId, 'messages', payload.messageId);
            // Soft-delete + shorten TTL to 30 days (deleted messages don't need full 1-year retention)
            const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
            await updateDoc(messageRef, {
                deleted: new Date(),
                expireAt: new Date(Date.now() + thirtyDaysMs)
            });
        },

        async _deleteRoomUser({ commit, rootState, dispatch }, payload) {
            const { removeRoomId, users } = payload;

            const promises = [];
            users.forEach((user) => {
                promises.push(updateDoc(doc(rootState.db, 'chatrooms', removeRoomId), {
                    users: arrayRemove(user.employeeID),
                    [`unreadMap.${user.employeeID}`]: deleteField()
                }));
            });

            await Promise.all(promises);
        },

        async _deleteRoom({ commit, rootState, dispatch }, payload) {
            const { removeRoomId } = payload;
            await deleteDoc(doc(rootState.db, 'chatrooms', removeRoomId));
        },

        async _updateMessageFileUrl({ rootState }, { roomId, messageId, url }) {
            const messageRef = doc(rootState.db, 'chatrooms', roomId, 'messages', messageId);
            await updateDoc(messageRef, { 'file.url': url });
        },

        async _updateRoomName({ commit, rootState }, { roomId, newName }) {
            const roomRef = doc(rootState.db, 'chatrooms', roomId);
            await updateDoc(roomRef, {
                roomName: newName,
                lastUpdated: serverTimestamp()
            });
            commit('setRoomName', { roomId, newName });
        },

        // Team Chat Export — admin-only feature. Async-fires the export job
        // on the backend (conversations-service chatExportCreate). The
        // backend emails the requester when the job finishes — no need to
        // track status in Vuex.
        // Payload: { siteId, scope, roomId?, format, includeAttachments,
        //            requesterEmail, requesterName?, siteName?, timezone? }
        // Returns: { ok: true, exportId } on 202.
        async _startChatExport(_ctx, payload) {
            const response = await axios.post('/chat-exports', payload);
            return response.data;
        }
    }
};